import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Button,
  Skeleton,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  Paper,
  Container,
  alpha,
  Fade,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  HomeWork,
  CheckCircle,
  Schedule,
  Block,
  Apartment,
  Search,
  Close,
  Inbox,
} from "@mui/icons-material";
import { GetProperty } from "../../../redux/reducer/GetPropertyDetails";
import { GetSystemSettings } from "../../../redux/reducer/SystemSettings";
import Wrapper from "../../../components/Wrapper";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import "./MyProperty.css";
import { toast, ToastContainer } from "react-toastify";
import { FlatRegisters } from "../../../redux/reducer/flatRegister";
import RecordNotFound from "../../../components/RecordNotFound";
import PropertyCard from "./components/PropertyCard";
import PropertyFormModal from "./components/PropertyFormModal";
import {
  PropertyAddedDialog,
  PropertyPipelineBanner,
} from "./components/PropertySetupGuide";
import { useNavigate } from "react-router-dom";
import { useTheme as useAppTheme } from "../../../context/ThemeContext";

export default function MyProperty() {
  /* The page used to be painted in a fixed crimson. It now follows whichever
     theme the owner picked, so the sidebar and the content agree. */
  const { theme: appTheme } = useAppTheme();
  const accent = appTheme.primary;
  const accentHover = appTheme.primaryHover;

  const [searchParams, setSearchParams] = useSearchParams();
  const [flats, setFlats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingFlat, setEditingFlat] = useState(null);
  const [tabValue, setTabValue] = useState(searchParams.get("tab") || "all");
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || ""
  );
  const [filteredFlats, setFilteredFlats] = useState([]);
  const [originalValues, setOriginalValues] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  /* Set right after a property is created, so the next-step dialog can take
     the owner to settings for exactly that flat. */
  const [addedFlatId, setAddedFlatId] = useState(null);
  const [showAddedDialog, setShowAddedDialog] = useState(false);

  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);
  const loaderSate = useSelector((state) => state?.getPropertyDetails?.loading);
  const loaderSateFlatRegister = useSelector(
    (state) => state?.flatRegister?.loading
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchFlats = useCallback(
    async (showTabLoader = false) => {
      try {
        if (showTabLoader) {
          setTabLoading(true);
        } else {
          setLoading(true);
        }

        const res = await dispatch(
          GetProperty({
            userId: validateUser?.UserValidate?.data?.user?._id,
            from: "landlord",
          })
        );

        if (res?.payload?.data?.success && res?.payload?.data?.data) {
          setFlats(res.payload.data.data);
        } else {
          setFlats([]);
        }
      } catch (err) {
        console.error("Error fetching properties:", err);
        setFlats([]);
        toast.error("Failed to fetch properties");
      } finally {
        setLoading(false);
        setTabLoading(false);
      }
    },
    [dispatch, validateUser?.UserValidate?.data?.user?._id]
  );

  // Initial load
  useEffect(() => {
    if (validateUser?.UserValidate?.data?.user?._id) {
      fetchFlats(false);
    }
  }, [validateUser?.UserValidate?.data?.user?._id, fetchFlats]);

  // The admin's late-fee policy, for the (read-only) late-payment section of
  // the add/edit property form — see PropertyFormModal's LatePolicyField.
  useEffect(() => {
    dispatch(GetSystemSettings());
  }, [dispatch]);

  // Filter logic combining tab and search
  useEffect(() => {
    let filtered = [...flats];

    // Apply tab filter
    if (tabValue === "available") {
      filtered = filtered.filter(
        (f) => Number(f.is_rent) === 0 && f.is_approved
      );
    } else if (tabValue === "rented") {
      filtered = filtered.filter((f) => Number(f.is_rent) === 1);
    } else if (tabValue === "pending") {
      filtered = filtered.filter(
        (f) => Number(f.is_rent) === 0 && !f.is_approved && !f.is_rejected
      );
    } else if (tabValue === "rejected") {
      filtered = filtered.filter(
        (f) => Number(f.is_rent) === 0 && !f.is_approved && f.is_rejected
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((flat) => {
        const flatName = (flat.flatName || "").toLowerCase();
        const flatNumber = (flat.flatNumber || "").toLowerCase();
        const propertyAddress = (flat.propertyAddress || "").toLowerCase();
        const city = (flat.city || "").toLowerCase();
        const country = (flat.country || "").toLowerCase();
        const flatFloor = (flat.flatFloor || "").toString().toLowerCase();
        const flatRooms = (flat.flatRooms || "").toString().toLowerCase();
        const flatRent = (flat.flatRent || "").toString().toLowerCase();

        return (
          flatName.includes(query) ||
          flatNumber.includes(query) ||
          propertyAddress.includes(query) ||
          city.includes(query) ||
          country.includes(query) ||
          flatFloor.includes(query) ||
          flatRooms.includes(query) ||
          flatRent.includes(query)
        );
      });
    }

    setFilteredFlats(filtered);
  }, [tabValue, searchQuery, flats]);

  // Update URL params when tab or search changes
  useEffect(() => {
    const params = {};
    if (tabValue !== "all") {
      params.tab = tabValue;
    }
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    setSearchParams(params, { replace: true });
  }, [tabValue, searchQuery, setSearchParams]);

  const handleTabChange = async (_, newValue) => {
    setTabValue(newValue);
    // Fetch fresh data on tab change
    await fetchFlats(true);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleOpenModal = (flat = null) => {
    if (flat) {
      setEditingFlat(flat);
    } else {
      setEditingFlat(null);
    }
    setActiveStep(0);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingFlat(null);
    setOriginalValues(null);
    setActiveStep(0);
  };

  const hasFormChanged = (currentValues) => {
    if (!editingFlat || !originalValues) return false;

    const fieldsToCompare = [
      "flatName",
      "flatNumber",
      "flatFloor",
      "flatRooms",
      "flatToilet",
      "flatKitchen",
      "flatRent",
      "flatAdvance",
      "flatMaintenanceAmount",
      "flatMaintenanceFrequency",
      "flatMaintenanceDueMonth",
      "flatWaterAmount",
      "flatWaterFrequency",
      "flatWaterDueMonth",
      "flatSecurityAmount",
      "flatSecurityFrequency",
      "flatSecurityDueMonth",
      "flatTrashAmount",
      "flatTrashFrequency",
      "flatTrashDueMonth",
      "personAllowed",
      "country",
      "city",
      "propertyAddress",
      "lat",
      "lng",
      "mapAddress",
      "note",
      "is_rent",
      "is_active",
      "allowedFor",
      "customConditions",
      "rentDueDate",
      "noticePeriod",
      "rentIncrease",
      "leaseDuration",
      "petAllowed",
      "parkingAvailable",
      "furnished",
      "pool",
      "bathTub",
      "wifi",
      "pets",
      "parking",
      "balcony",
      "elevator",
      "gym",
      "fireExtinguisher",
      "firstAidKit",
      "smokeAlarm",
      "roof",
      // "bill_uploaded_by"
    ];

    for (const field of fieldsToCompare) {
      if (
        String(currentValues[field] || "") !==
        String(originalValues[field] || "")
      ) {
        return true;
      }
    }

    // Late-payment policy is an object, so it can't go through the String()
    // comparison above.
    if (
      JSON.stringify(currentValues.latePolicy || null) !==
      JSON.stringify(originalValues.latePolicy || null)
    ) {
      return true;
    }

    const currentTags = Array.isArray(currentValues.tags)
      ? [...currentValues.tags].sort().join(",")
      : "";
    const originalTags = Array.isArray(originalValues.tags)
      ? [...originalValues.tags].sort().join(",")
      : "";
    if (currentTags !== originalTags) {
      return true;
    }

    const currentConditions = Array.isArray(currentValues.landlordConditions)
      ? [...currentValues.landlordConditions].sort().join(",")
      : "";
    const originalConditions = Array.isArray(originalValues.landlordConditions)
      ? [...originalValues.landlordConditions].sort().join(",")
      : "";
    if (currentConditions !== originalConditions) {
      return true;
    }

    const currentPreviews = Array.isArray(currentValues.previews)
      ? currentValues.previews
      : [];
    const originalPreviews = Array.isArray(originalValues.previews)
      ? originalValues.previews
      : [];

    if (currentPreviews.length !== originalPreviews.length) {
      return true;
    }

    const hasNewFiles = currentPreviews.some(
      (url) =>
        (typeof url === "string" && url.startsWith("blob:")) ||
        url instanceof File
    );
    if (hasNewFiles) {
      return true;
    }

    const currentImageUrls = currentPreviews
      .filter((url) => typeof url === "string" && !url.startsWith("blob:"))
      .sort();
    const originalImageUrls = originalPreviews
      .filter((url) => typeof url === "string" && !url.startsWith("blob:"))
      .sort();

    if (currentImageUrls.length !== originalImageUrls.length) {
      return true;
    }

    for (let i = 0; i < currentImageUrls.length; i++) {
      if (currentImageUrls[i] !== originalImageUrls[i]) {
        return true;
      }
    }

    const currentImages = Array.isArray(currentValues.images)
      ? currentValues.images
      : [];
    const originalImages = Array.isArray(originalValues.images)
      ? originalValues.images
      : [];
    if (currentImages.length !== originalImages.length) {
      return true;
    }

    return false;
  };

  // Calculate stats
  const stats = {
    total: flats.length,
    available: flats.filter((f) => Number(f.is_rent) === 0 && f.is_approved)
      .length,
    rented: flats.filter((f) => Number(f.is_rent) === 1).length,
    pending: flats.filter((f) => !f.is_approved && !f.is_rejected).length,
    rejected: flats.filter((f) => f.is_rejected).length,
  };

  // Get no data message based on active tab
  const getNoDataMessage = () => {
    const messages = {
      all: {
        title: "No Properties Found",
        description: searchQuery
          ? `No properties match your search "${searchQuery}"`
          : "You haven't added any properties yet. Click 'Add New Property' to get started!",
        icon: <Inbox sx={{ fontSize: 80, color: "#cbd5e1" }} />,
      },
      available: {
        title: "No Available Properties",
        description: searchQuery
          ? `No available properties match your search "${searchQuery}"`
          : "You don't have any available properties at the moment. All properties are either rented, pending approval, or rejected.",
        icon: <CheckCircle sx={{ fontSize: 80, color: "#cbd5e1" }} />,
      },
      rented: {
        title: "No Rented Properties",
        description: searchQuery
          ? `No rented properties match your search "${searchQuery}"`
          : "You don't have any rented properties yet. Once tenants move in, they'll appear here.",
        icon: <HomeWork sx={{ fontSize: 80, color: "#cbd5e1" }} />,
      },
      pending: {
        title: "No Pending Properties",
        description: searchQuery
          ? `No pending properties match your search "${searchQuery}"`
          : "Great! You don't have any properties waiting for approval. All your properties have been reviewed.",
        icon: <Schedule sx={{ fontSize: 80, color: "#cbd5e1" }} />,
      },
      rejected: {
        title: "No Rejected Properties",
        description: searchQuery
          ? `No rejected properties match your search "${searchQuery}"`
          : "You don't have any rejected properties. All submitted properties have been approved or are pending review.",
        icon: <Block sx={{ fontSize: 80, color: "#cbd5e1" }} />,
      },
    };

    return messages[tabValue] || messages.all;
  };

  const noDataInfo = getNoDataMessage();

  return (
    <>
      {(loaderSate || loaderSateFlatRegister) && (
        <div className="loader-overlay">
          <CircularProgress />
        </div>
      )}
      <Wrapper open={open} />
      <Box
        className={`${open ? "sidebar-open" : "sidebar-closed"}`}
        sx={{
          minHeight: "100vh",
          /* transparent so the owner canvas behind it shows through */
          bgcolor: "transparent",
          pb: 4,
        }}
      >
        <Container maxWidth="xl">
          {/* Header Section */}
          <Box
            sx={{
              pt: { xs: 3, md: 4 },
              pb: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  mb: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <HomeWork sx={{ fontSize: 36, color: accent }} />
                My Properties
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track all your rental properties
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => handleOpenModal()}
              sx={{
                bgcolor: accent,
                px: 3,
                py: 1.2,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                boxShadow: `0 10px 22px -14px ${accent}`,
                "&:hover": {
                  bgcolor: accentHover,
                  boxShadow: `0 14px 26px -14px ${accent}`,
                },
              }}
            >
              Add New Property
            </Button>
          </Box>

          {/* Where the listings stand, and the one action that moves them on */}
          {!loading && flats.length > 0 && (
            <PropertyPipelineBanner
              flats={flats}
              onApplySettings={() => navigate("/properties-setting")}
              onViewRejected={() => setTabValue("rejected")}
            />
          )}

          {/* Stats Cards */}
          <Fade in={!loading} timeout={500}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {loading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <Grid item xs={6} sm={4} md={2.4} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: "white",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <Skeleton
                          variant="rectangular"
                          width={48}
                          height={48}
                          sx={{ borderRadius: 2, mb: 1.5 }}
                        />
                        <Skeleton width="60%" height={32} />
                        <Skeleton width="80%" height={20} sx={{ mt: 0.5 }} />
                      </Paper>
                    </Grid>
                  ))
                : [
                    {
                      label: "Total Properties",
                      value: stats.total,
                      icon: <Apartment />,
                      color: accent,
                      bgColor: `${accent}14`,
                    },
                    {
                      label: "Available",
                      value: stats.available,
                      icon: <CheckCircle />,
                      color: "#22c55e",
                      bgColor: "#dcfce7",
                    },
                    {
                      label: "Rented",
                      value: stats.rented,
                      icon: <HomeWork />,
                      color: "#3b82f6",
                      bgColor: "#dbeafe",
                    },
                    {
                      label: "Pending",
                      value: stats.pending,
                      icon: <Schedule />,
                      color: "#f59e0b",
                      bgColor: "#fef3c7",
                    },
                    {
                      label: "Rejected",
                      value: stats.rejected,
                      icon: <Block />,
                      color: "#ef4444",
                      bgColor: "#fee2e2",
                    },
                  ].map((stat, index) => (
                    <Grid item xs={6} sm={4} md={2.4} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: "white",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: stat.bgColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1.5,
                          }}
                        >
                          <Box sx={{ color: stat.color, fontSize: 24 }}>
                            {stat.icon}
                          </Box>
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
            </Grid>
          </Fade>

          {/* Search Bar */}
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by property name, number, address, city, floor, rooms, or rent..."
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={loading || tabLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#64748b" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={handleClearSearch}
                      disabled={loading || tabLoading}
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        color: "#64748b",
                        "&:hover": {
                          bgcolor: "#f1f5f9",
                        },
                      }}
                    >
                      <Close fontSize="small" />
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#f8fafc",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: accent,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: accent,
                  },
                },
              }}
            />
            {searchQuery && !loading && !tabLoading && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Showing {filteredFlats.length} result
                {filteredFlats.length !== 1 ? "s" : ""} for "{searchQuery}"
              </Typography>
            )}
          </Paper>

          {/* Tabs Section */}
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: "1px solid #e5e7eb",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  minHeight: 56,
                  color: "#64748b",
                  "&.Mui-selected": {
                    color: accent,
                  },
                  "&.Mui-disabled": {
                    opacity: 0.6,
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: accent,
                  height: 3,
                },
              }}
            >
              <Tab
                label={`All Properties (${stats.total})`}
                value="all"
                disabled={loading || tabLoading}
              />
              <Tab
                label={`Available (${stats.available})`}
                value="available"
                disabled={loading || tabLoading}
              />
              <Tab
                label={`Rented (${stats.rented})`}
                value="rented"
                disabled={loading || tabLoading}
              />
              <Tab
                label={`Pending (${stats.pending})`}
                value="pending"
                disabled={loading || tabLoading}
              />
              <Tab
                label={`Rejected (${stats.rejected})`}
                value="rejected"
                disabled={loading || tabLoading}
              />
            </Tabs>
          </Paper>

          {/* Properties Grid */}
          <Box>
            {loading || tabLoading ? (
              <Grid container spacing={2.5}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        height={200}
                        sx={{ bgcolor: "#f1f5f9" }}
                      />
                      <Box sx={{ p: 2 }}>
                        <Skeleton width="80%" height={28} />
                        <Skeleton width="60%" height={24} sx={{ mt: 1 }} />
                        <Skeleton width="90%" height={20} sx={{ mt: 1 }} />
                        <Skeleton width="70%" height={20} sx={{ mt: 1 }} />
                        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                          <Skeleton
                            width={80}
                            height={32}
                            sx={{ borderRadius: 1 }}
                          />
                          <Skeleton
                            width={80}
                            height={32}
                            sx={{ borderRadius: 1 }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : filteredFlats && filteredFlats?.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  py: 8,
                  borderRadius: 2,
                  bgcolor: "white",
                  border: "1px solid #e5e7eb",
                }}
              >
                <Box
                  sx={{
                    textAlign: "center",
                    maxWidth: 500,
                    mx: "auto",
                    px: 3,
                  }}
                >
                  {noDataInfo.icon}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      mt: 3,
                      mb: 1,
                    }}
                  >
                    {noDataInfo.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3, lineHeight: 1.6 }}
                  >
                    {noDataInfo.description}
                  </Typography>
                  {searchQuery ? (
                    <Button
                      variant="contained"
                      onClick={handleClearSearch}
                      sx={{
                        bgcolor: accent,
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: accentHover,
                        },
                      }}
                    >
                      Clear Search
                    </Button>
                  ) : (
                    tabValue === "all" && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenModal()}
                        sx={{
                          bgcolor: accent,
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": {
                            bgcolor: accentHover,
                          },
                        }}
                      >
                        Add Your First Property
                      </Button>
                    )
                  )}
                </Box>
              </Paper>
            ) : (
              <Fade in={true} timeout={500}>
                <Grid container spacing={2.5}>
                  {filteredFlats &&
                    filteredFlats.length > 0 &&
                    filteredFlats.map((flat) => (
                      <PropertyCard
                        key={flat._id}
                        flat={flat}
                        handleOpen={handleOpenModal}
                        userId={validateUser?.UserValidate?.data?.user?._id}
                        validateUser={validateUser}
                        setFlats={setFlats}
                      />
                    ))}
                </Grid>
              </Fade>
            )}
          </Box>
        </Container>

        {openModal && (
          <PropertyFormModal
            open={openModal}
            handleClose={handleCloseModal}
            editingFlat={editingFlat}
            validateUser={validateUser}
            dispatch={dispatch}
            FlatRegisters={FlatRegisters}
            fetchFlats={fetchFlats}
            toast={toast}
            hasFormChanged={hasFormChanged}
            originalValues={originalValues}
            setOriginalValues={setOriginalValues}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            onCreated={(flatId) => {
              setAddedFlatId(flatId);
              setShowAddedDialog(true);
            }}
          />
        )}

        <PropertyAddedDialog
          open={showAddedDialog}
          onClose={() => setShowAddedDialog(false)}
          onApplySettings={() => {
            setShowAddedDialog(false);
            /* The create response is the first choice for the id; if the API
               doesn't return the flat, fall back to the freshly refetched list
               — the new flat is the one still without settings. */
            const target =
              addedFlatId ||
              flats.find(
                (f) =>
                  !f.is_property_settings &&
                  !f.is_rejected &&
                  Number(f.is_rent) !== 1
              )?._id;
            navigate(
              target
                ? `/properties-setting?flat=${target}`
                : "/properties-setting"
            );
          }}
        />
      </Box>
      <ToastContainer />
    </>
  );
}
